import { type FC, type SyntheticEvent, useCallback, useEffect, useMemo } from "react";
import { type DropdownItemProps, type DropdownProps } from "semantic-ui-react";
import cn from "classnames";

import { DropdownWrapper } from "../dropdowns";
import FieldLabel from "../forms/FieldLabel";
import { type AvailableDomainsConfigurationState } from "../../features/Accounts/state/slices/accountDomainsSlice";
import { type AssignDomain } from "../../features/Accounts/PackAssignmentModal/types";

import styles from "./domainsSection.module.scss";

export interface DomainsSectionProps {
  packId: number;
  setSelectedDomainAction: (selectedDomain: AssignDomain) => void;
  setSelectedDomainTypeAction: (selectedDomainType: number) => void;
  availableDomains: AvailableDomainsConfigurationState;
  getAvailableDomains: () => void;
}

export const DomainsSection: FC<DomainsSectionProps> = ({
  packId,
  availableDomains,
  getAvailableDomains,
  setSelectedDomainAction,
  setSelectedDomainTypeAction,
}) => {
  useEffect(() => {
    getAvailableDomains();
  }, [getAvailableDomains]);

  useEffect(() => {
    if (!availableDomains.selectedDomain?.domainType && availableDomains.domainTypes.length > 0) {
      setSelectedDomainTypeAction(availableDomains.domainTypes[0].id);
    }
  }, [availableDomains.domainTypes, availableDomains.selectedDomain?.domainType, setSelectedDomainTypeAction]);

  const domainSource = useMemo(() => {
    if (availableDomains.selectedDomain?.domainType === undefined) return [];
    const domains = availableDomains.domains?.[availableDomains.selectedDomain?.domainType];
    return domains?.length ? domains : availableDomains.domainPools || [];
  }, [availableDomains]);

  const domainTypesOptions: DropdownItemProps[] = useMemo(
    () => availableDomains.domainTypes.map(({ id, name }) => ({ text: name, value: id })),
    [availableDomains.domainTypes],
  );

  const domainsOptions: DropdownItemProps[] = useMemo(
    () => domainSource.map((d) => ({ text: d.name, value: d.id })),
    [domainSource],
  );

  const handleDomainTypeChange = useCallback(
    (_: SyntheticEvent<HTMLElement>, data: DropdownProps) => {
      setSelectedDomainTypeAction(data.value as number);
    },
    [setSelectedDomainTypeAction],
  );

  const handleDomainChange = useCallback(
    (_: SyntheticEvent<HTMLElement>, data: DropdownProps) => {
      const currentValue = data.value as number;
      const domainId = availableDomains.useDomains ? currentValue ?? undefined : undefined;
      const domainPoolId = availableDomains.useDomains ? undefined : currentValue ?? undefined;
      setSelectedDomainAction({
        domainId,
        domainPoolId,
        isAssigned: !domainId || !domainPoolId,
        domainType: availableDomains.selectedDomain?.domainType,
        packId,
      });
    },
    [availableDomains.selectedDomain?.domainType, availableDomains.useDomains, packId, setSelectedDomainAction],
  );

  return (
    <>
      <div className={styles.field}>
        <FieldLabel label="Domain Type" className={styles.label} />
        <DropdownWrapper
          blur
          selection
          required
          data-testid="domain-type-dropdown"
          selected={availableDomains.selectedDomain?.domainType || null}
          handleOptionChange={handleDomainTypeChange}
          items={domainTypesOptions}
          disabled={domainTypesOptions.length <= 1}
          className={cn("field", styles.dropdown)}
        />
      </div>
      <div className={styles.field}>
        <FieldLabel label={availableDomains.useDomains ? "Domain" : "Domain Pool"} className={styles.label} />
        <DropdownWrapper
          blur
          selection
          required
          data-testid="domain-dropdown"
          selected={availableDomains.selectedDomain?.domainId ?? availableDomains.selectedDomain?.domainPoolId ?? null}
          handleOptionChange={handleDomainChange}
          items={domainsOptions}
          placeholder={availableDomains.useDomains ? "Select domain" : "Select pool"}
          className={cn("field", styles.dropdown)}
        />
      </div>
    </>
  );
};
